import type { MutableRefObject, ReactNode } from "react";
import React from "react";
import { cn } from "@rosette/ts/src/util/cn.js";
import BurgerIcon from "@rosette/web_components/src/icon/BurgerIcon.js";
import CloseIcon from "@rosette/web_components/src/icon/CloseIcon.js";


interface MenuLayerProps {
    isMobile: boolean;
    children?: ReactNode;
}

/**
 * A full-size layer over the game that holds the menu buttons and menus.
 */
export function MenuLayer({ isMobile, children }: MenuLayerProps) {
    return (
        <div className={cn(
            "absolute inset-0 w-full h-full flex text-base pointer-events-none",
            isMobile ? "items-end pb-[7em]" : "items-center pt-[6em]",
        )}>
            {children}
        </div>
    );
}


interface MenuCornerButtonProps {
    side: "left" | "right";
    isActive: boolean;
    isMobile: boolean;
    onClick: () => void;
}

/**
 * The menu (left) and leave (right) buttons in the corners of the game.
 */
export function MenuCornerButton({
    side, isActive, isMobile, onClick,
}: MenuCornerButtonProps) {
    const iconClassName = cn(
        "absolute left-1/2 top-1/2 -translate-1/2",
        isActive ? "fill-text-on-light" : "fill-text",
        side === "left" ? "w-[62%] h-[62%]" : "w-[76%] h-[76%]",
    );
    return (
        <button
            type="button"
            aria-label={side === "left" ? "Menu" : "Exit"}
            aria-expanded={isActive}
            className={cn(
                "absolute z-100 m-[1.2em] pb-[0.4em] rounded-[1em] cursor-pointer pointer-events-auto",
                "transition-transform duration-100 ease-in-out hover:scale-[1.075] active:scale-none",
                isMobile ? "bottom-0 w-[3.5em] h-[3.5em]" : "top-0 w-[4em] h-[4em]",
                side === "left" ? "left-0" : "right-0",
                isActive
                    ? "bg-white opacity-85"
                    : cn("bg-surface-sunken", isMobile ? "opacity-65" : "opacity-75"),
            )}
            // The board rolls and moves on Space and Enter anywhere in the document,
            // unless the event is defaultPrevented. Clicks leave focus on the page so
            // Space still rolls, and a focused button handles its own key presses.
            onMouseDown={e => e.preventDefault()}
            onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    onClick();
                }
            }}
            onClick={onClick}>

            {side === "left" && (
                <BurgerIcon className={iconClassName} />
            )}
            {side === "right" && (
                <CloseIcon className={iconClassName} />
            )}
        </button>
    );
}


interface MenuDarkOverlayProps {
    className?: string;
}

export function MenuDarkOverlay({ className }: MenuDarkOverlayProps) {
    return (
        <div className={cn(
            "absolute inset-0 w-full h-full z-98 bg-scrim",
            className ?? null,
        )} />
    );
}


interface MenuOptionsProps {
    contentRef?: MutableRefObject<HTMLDivElement | null>;
    className?: string;
    children?: ReactNode;
}

/**
 * The column of cards and options in a menu.
 */
export function MenuOptions({ contentRef, className, children }: MenuOptionsProps) {
    return (
        <div
            className={cn(
                "flex flex-col items-center w-full min-w-[min(28em,100%)] max-w-[28em] gap-6 md:gap-4",
                className ?? null,
            )}
            ref={contentRef}>

            {children}
        </div>
    );
}


/**
 * A menu's buttons: one width, em padding so they scale with the game's
 * text, and rounder corners than a site button.
 */
export const MENU_BUTTON = "w-full max-w-[14.5em] px-6 py-[0.5em] rounded-xl";


export const MENU_PANEL_TITLE = "mt-0 text-4xl text-strong";
